

// src/components/forms/QuoteForm.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BuildingStorefrontIcon,
  CubeIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowLeftIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

const QuoteForm = () => {
  const navigate = useNavigate();
  const BASEURL = import.meta.env.VITE_BASE_URL;

  // core state
  const [brands, setBrands]               = useState([]);
  const [models, setModels]               = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [currentStep, setCurrentStep]     = useState(0);

  // search
  const [searchBrand, setSearchBrand] = useState("");
  const [searchModel, setSearchModel] = useState("");

  // brand pagination
  const [brandPage, setBrandPage]               = useState(0);
  const [brandTotal, setBrandTotal]             = useState(0);
  const [brandHasMore, setBrandHasMore]         = useState(false);
  const [loadingMoreBrands, setLoadingMoreBrands] = useState(false);

  // model pagination
  const [modelPage, setModelPage]               = useState(0);
  const [modelTotal, setModelTotal]             = useState(0);
  const [modelHasMore, setModelHasMore]         = useState(false);
  const [loadingMoreModels, setLoadingMoreModels] = useState(false);
const [displayBrand, setDisplayBrand] = useState("");  // ← ADD THIS LINE
const [displayModel, setDisplayModel]  = useState(""); // ← ADD THIS LINE

  // ui
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const brandSearchTimer   = useRef(null);
  const modelSearchTimer   = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef       = useRef(true);
  const imageCache         = useRef(new Set());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      clearTimeout(brandSearchTimer.current);
      clearTimeout(modelSearchTimer.current);
    };
  }, []);

  const createAbortController = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  };

  const joinUrl = (base, p) => {
    if (!p) return null;
    if (p.startsWith("http")) return p;
    const cleanBase = base.replace(/\/+$/, "");
    const cleanPath = p.replace(/^\/+/, "");
    return `${cleanBase}/${cleanPath}`;
  };

  const getFallbackImage = (w = 60, h = 60) =>
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
        <rect width="${w}" height="${h}" fill="#EFF6FF" rx="8"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#93C5FD" font-size="10" font-weight="700">NO IMG</text>
      </svg>`
    )}`;

  // ── fetchBrands: page=0 replaces list, page>0 appends ────────────────────────
  const fetchBrands = useCallback(async (page = 0, search = "", append = false) => {
    page === 0 ? setLoading(true) : setLoadingMoreBrands(true);
    setError(null);
    try {
      const controller = createAbortController();
      const res = await axios.get(`${BASEURL}/brand/list`, {
        params: { page, limit: 24, search },
        headers: { Accept: "application/json" },
        timeout: 15000,
        signal: controller.signal,
      });
      if (!isMountedRef.current) return;
      const data = Array.isArray(res.data.brands) ? res.data.brands : [];
      const mapped = data.map((b) => ({
        id:    b.brand_id,
        name:  b.brand_name,
        image: b.logo ? joinUrl(BASEURL, b.logo) : null,
      }));
      setBrands((prev) => append ? [...prev, ...mapped] : mapped);
      setBrandPage(page);
      setBrandTotal(res.data.total ?? 0);
      setBrandHasMore(res.data.hasMore ?? false);
    } catch (err) {
      if (!isMountedRef.current) return;
      if (axios.isCancel(err) || err.name === "AbortError") return;
      setError("Failed to load brands. Please try again.");
    } finally {
      if (isMountedRef.current) { setLoading(false); setLoadingMoreBrands(false); }
    }
  }, [BASEURL]);

  // ── fetchModelsByBrand ────────────────────────────────────────────────────────
  const fetchModelsByBrand = useCallback(async (brandId, page = 0, search = "", append = false) => {
    page === 0 ? setLoading(true) : setLoadingMoreModels(true);
    setError(null);
    try {
      const controller = createAbortController();
      const res = await axios.get(`${BASEURL}/model/${brandId}`, {
        params: { page, limit: 30, search },
        headers: { Accept: "application/json" },
        timeout: 15000,
        signal: controller.signal,
      });
      if (!isMountedRef.current) return;
      const data = Array.isArray(res.data.models) ? res.data.models : [];
      const mapped = data.map((m) => ({ id: m.id, name: m.model_name }));
      setModels((prev) => append ? [...prev, ...mapped] : mapped);
      setModelPage(page);
      setModelTotal(res.data.total ?? 0);
      setModelHasMore(res.data.hasMore ?? false);
      if (page === 0 && mapped.length === 0) setError("No models found for selected brand.");
    } catch (err) {
      if (!isMountedRef.current) return;
      if (axios.isCancel(err) || err.name === "AbortError") return;
      setError("Failed to load models. Please try again.");
    } finally {
      if (isMountedRef.current) { setLoading(false); setLoadingMoreModels(false); }
    }
  }, [BASEURL]);

  // ── debounced brand search ────────────────────────────────────────────────────
const handleBrandSearchChange = (e) => {
  const val = e.target.value;
  setDisplayBrand(val);  // ✅ Only updates display, no re-render chain
  
  clearTimeout(brandSearchTimer.current);
  brandSearchTimer.current = setTimeout(() => {
    setSearchBrand(val);  // ✅ Only NOW trigger API chain
    setBrands([]);
    fetchBrands(0, val, false);
  }, 1000);
};

// ── FIXED: Model search (focus-safe) ──────────────────────────────────
const handleModelSearchChange = (e) => {
  const val = e.target.value;
  setDisplayModel(val);  // ✅ Only updates display, no re-render chain
  
  clearTimeout(modelSearchTimer.current);
  modelSearchTimer.current = setTimeout(() => {
    setSearchModel(val);  // ✅ Only NOW trigger API chain
    if (selectedBrand) {
      setModels([]);
      fetchModelsByBrand(selectedBrand.id, 0, val, false);
    }
  }, 1000);
};

  const handleBrandClick = async () => {
    setCurrentStep(1);
    if (brands.length === 0) await fetchBrands(0, "", false);
  };

  const handleBrandSelect = async (brand) => {
    setSelectedBrand(brand);
    setSelectedModel(null);
    setModels([]);
    setModelPage(0);
    setModelHasMore(false);
    setSearchModel("");
    setError(null);
    await fetchModelsByBrand(brand.id, 0, "", false);
    setCurrentStep(2);
  };

  const handleModelSelect = (model) => {
    setSelectedModel(model);
    setError(null);
    setCurrentStep(3);
  };

  const handleCheckPrices = async () => {
    if (!selectedBrand || !selectedModel) { setError("Please select brand and model."); return; }
    setLoading(true);
    setError(null);
    try {
      const controller = createAbortController();
      const res = await axios.get(`${BASEURL}/admin/service/filter`, {
        params: { brand_id: selectedBrand.id, model_id: selectedModel.id },
        headers: { Accept: "application/json" },
        timeout: 15000,
        signal: controller.signal,
      });
      if (!isMountedRef.current) return;
      const services = Array.isArray(res.data) ? res.data : res.data?.services || [];
      if (!services.length) { setError("No services available for this brand/model."); return; }
      navigate("/service-packages", {
        state: {
          bikeData: {
            brand: selectedBrand.name, model: selectedModel.name,
            image: "/images/default-bike.png",
            brandLogo: selectedBrand.image || "",
            brandId: selectedBrand.id, modelId: selectedModel.id, services,
          },
        },
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      if (axios.isCancel(err) || err.name === "AbortError") return;
      setError(err?.response?.data?.message || "Failed to load services. Please try again.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleImageError = (e, imageUrl) => {
    if (!imageCache.current.has(imageUrl)) {
      imageCache.current.add(imageUrl);
      e.currentTarget.src = getFallbackImage(60, 60);
    }
  };

  const steps = [
    { number: 1, title: "Brand", icon: BuildingStorefrontIcon },
    { number: 2, title: "Model", icon: CubeIcon },
    { number: 3, title: "Confirm", icon: CheckCircleIcon },
  ];

  // ── Reusable: Error Banner ────────────────────────────────────────────────────
  const ErrorBanner = () => !error ? null : (
    <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
      <XMarkIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
      <p className="text-red-700 text-sm font-medium">{error}</p>
    </div>
  );

  // ── Reusable: Load More Button ────────────────────────────────────────────────
  const LoadMoreBtn = ({ onClick, isLoading, shown, total }) => (
    <button onClick={onClick} disabled={isLoading}
      className="w-full py-3 border-2 border-blue-200 rounded-xl text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 mt-2">
      {isLoading
        ? <><div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" /><span>Loading...</span></>
        : <span>Load More ({shown} of {total} shown)</span>}
    </button>
  );

  // ── Reusable: Skeleton Grid ────────────────────────────────────────────────────
  const BrandSkeleton = () => (
    <div className="grid grid-cols-3 gap-3">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="p-4 rounded-2xl border-2 border-gray-100 animate-pulse">
          <div className="w-16 h-16 bg-gray-200 rounded-xl mx-auto mb-3" />
          <div className="h-3 bg-gray-200 rounded mx-auto w-3/4" />
        </div>
      ))}
    </div>
  );

  const ModelSkeleton = () => (
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="p-4 rounded-2xl border-2 border-gray-100 animate-pulse flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-xl flex-shrink-0" />
          <div className="flex-1"><div className="h-3 bg-gray-200 rounded w-3/4" /></div>
        </div>
      ))}
    </div>
  );

  // ── Progress Stepper ──────────────────────────────────────────────────────────
  const ProgressStepper = () => {
    if (currentStep === 0) return null;
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between relative px-4">
          <div className="absolute top-5 left-0 w-full h-0.5 bg-blue-200 -z-10">
            <div className="h-full bg-blue-600 transition-all duration-500"
              style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }} />
          </div>
          {steps.map((step) => {
            const StepIcon = step.icon;
            const isCompleted = currentStep > step.number;
            const isCurrent   = currentStep === step.number;
            return (
              <div key={step.number} className="flex flex-col items-center flex-1 relative z-10">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all border-2 shadow-md
                  ${isCompleted || isCurrent ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}>
                  {isCompleted
                    ? <CheckCircleIcon className="h-5 w-5 text-white" />
                    : <StepIcon className={`h-5 w-5 ${isCurrent ? "text-white" : "text-gray-400"}`} />}
                </div>
                <span className={`text-xs mt-2 font-semibold text-center
                  ${isCurrent || isCompleted ? "text-blue-600" : "text-gray-400"}`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Step 0: Initial ───────────────────────────────────────────────────────────
  const InitialStep = () => (
    <div className="space-y-6 animate-fadeIn">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <SparklesIcon className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Find Your Service</h2>
        <p className="text-sm text-gray-600">Select your bike to view available services</p>
      </div>
      <div className="space-y-3">
        <button onClick={handleBrandClick}
          className="w-full p-5 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl hover:from-blue-100 hover:to-blue-200 hover:border-blue-300 transition-all duration-200 text-left group">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-md">
              <BuildingStorefrontIcon className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-blue-600 mb-1">Select Brand</p>
              <p className="text-xs text-gray-600">{selectedBrand ? selectedBrand.name : "Choose your bike brand"}</p>
            </div>
            <ChevronRightIcon className="h-5 w-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>
        <button onClick={() => selectedBrand && setCurrentStep(2)} disabled={!selectedBrand}
          className={`w-full p-5 bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 rounded-2xl transition-all duration-200 text-left group
            ${selectedBrand ? "hover:from-blue-50 hover:to-blue-100 hover:border-blue-200 cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 ${selectedBrand ? "bg-blue-600" : "bg-gray-400"} rounded-xl flex items-center justify-center flex-shrink-0 transition-all shadow-md
              ${selectedBrand ? "group-hover:scale-110" : ""}`}>
              <CubeIcon className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <p className={`text-sm font-bold mb-1 ${selectedBrand ? "text-blue-600" : "text-gray-500"}`}>Select Model</p>
              <p className="text-xs text-gray-600">{selectedModel ? selectedModel.name : "Choose your bike model"}</p>
            </div>
            <ChevronRightIcon className={`h-5 w-5 ${selectedBrand ? "text-blue-600" : "text-gray-400"} transition-transform
              ${selectedBrand ? "group-hover:translate-x-1" : ""}`} />
          </div>
        </button>
      </div>
      {selectedBrand && selectedModel && (
        <button onClick={handleCheckPrices} disabled={loading}
          className="w-full mt-6 p-5 bg-blue-600 text-white rounded-2xl font-bold text-base hover:bg-blue-700 hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
          {loading
            ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Loading Services...</span></>
            : <><span>VIEW AVAILABLE SERVICES</span><ChevronRightIcon className="h-5 w-5" /></>}
        </button>
      )}
      <ErrorBanner />
    </div>
  );

  // ── Step 1: Brand Selection ───────────────────────────────────────────────────
  const BrandSelectionStep = () => (
    <div className="space-y-5 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Choose Your Brand</h2>
          <p className="text-sm text-gray-600">
            {brandTotal > 0 ? `${brandTotal} brands available` : "Select from popular brands"}
          </p>
        </div>
        <button onClick={() => setCurrentStep(0)}
          className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-xl transition-colors font-semibold">
          <ArrowLeftIcon className="h-4 w-4" />Back
        </button>
      </div>

      {/* Server-side debounced search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input type="text" placeholder="Search brands..."
          value={displayBrand} onChange={handleBrandSearchChange}
          className="w-full pl-12 pr-10 py-3.5 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:outline-none focus:bg-white transition-all text-gray-900 placeholder:text-gray-400" />
        {searchBrand && (
          <button onClick={() => { setDisplayBrand(""); fetchBrands(0, "", false); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <ErrorBanner />

      {loading ? <BrandSkeleton /> : brands.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <MagnifyingGlassIcon className="h-10 w-10 text-gray-400" />
          </div>
          <p className="text-gray-600 text-sm font-medium">No brands found</p>
          {searchBrand && <p className="text-gray-400 text-xs mt-1">Try a different search term</p>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {brands.map((brand) => (
              <button key={brand.id} onClick={() => handleBrandSelect(brand)} disabled={loading}
                className="group p-4 bg-white border-2 border-gray-200 rounded-2xl hover:border-blue-600 hover:shadow-lg transition-all duration-200 disabled:opacity-50">
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-xl p-2 group-hover:bg-blue-50 transition-all">
                    <img src={brand.image || getFallbackImage(64, 64)} alt={brand.name}
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => handleImageError(e, brand.image)} loading="lazy" />
                  </div>
                  <span className="text-xs text-center font-semibold text-gray-900 line-clamp-2 leading-tight">
                    {brand.name}
                  </span>
                </div>
              </button>
            ))}
          </div>
          {brandHasMore && (
            <LoadMoreBtn
              onClick={() => fetchBrands(brandPage + 1, searchBrand, true)}
              isLoading={loadingMoreBrands}
              shown={brands.length}
              total={brandTotal} />
          )}
        </>
      )}
    </div>
  );

  // ── Step 2: Model Selection ───────────────────────────────────────────────────
  const ModelSelectionStep = () => (
    <div className="space-y-5 animate-fadeIn">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Select Model</h2>
          <p className="text-sm text-gray-600">
            {selectedBrand?.name}{modelTotal > 0 ? ` • ${modelTotal} models` : ""}
          </p>
        </div>
        <button onClick={() => { setCurrentStep(1); setSelectedModel(null); }}
          className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-xl transition-colors font-semibold">
          <ArrowLeftIcon className="h-4 w-4" />Back
        </button>
      </div>

      {selectedBrand && (
        <div className="p-4 bg-blue-50 rounded-2xl border-2 border-blue-100 flex items-center gap-4">
          <div className="w-14 h-14 bg-white rounded-xl p-2 flex items-center justify-center flex-shrink-0 shadow-sm">
            <img src={selectedBrand.image || getFallbackImage(56, 56)} alt={selectedBrand.name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => handleImageError(e, selectedBrand.image)} />
          </div>
          <div>
            <p className="text-xs text-blue-600 font-bold uppercase mb-1">Selected Brand</p>
            <p className="text-sm font-bold text-gray-900">{selectedBrand.name}</p>
          </div>
        </div>
      )}

      {/* Server-side debounced search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input type="text" placeholder="Search models..."
          value={displayModel} onChange={handleModelSearchChange}
          className="w-full pl-12 pr-10 py-3.5 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:outline-none focus:bg-white transition-all text-gray-900 placeholder:text-gray-400" />
        {searchModel && (
          <button onClick={() => { setDisplayModel(""); if (selectedBrand) fetchModelsByBrand(selectedBrand.id, 0, "", false); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <ErrorBanner />

      {loading ? <ModelSkeleton /> : models.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <CubeIcon className="h-10 w-10 text-gray-400" />
          </div>
          <p className="text-gray-600 text-sm font-medium">No models found</p>
          {searchModel && <p className="text-gray-400 text-xs mt-1">Try a different search term</p>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {models.map((model) => (
              <button key={model.id} onClick={() => handleModelSelect(model)} disabled={loading}
                className="group p-4 bg-white border-2 border-gray-200 rounded-2xl hover:border-blue-600 hover:shadow-lg transition-all duration-200 disabled:opacity-50 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex-shrink-0 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-all">
                    <CubeIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 line-clamp-2">{model.name}</span>
                </div>
              </button>
            ))}
          </div>
          {modelHasMore && (
            <LoadMoreBtn
              onClick={() => fetchModelsByBrand(selectedBrand.id, modelPage + 1, searchModel, true)}
              isLoading={loadingMoreModels}
              shown={models.length}
              total={modelTotal} />
          )}
        </>
      )}
    </div>
  );

  // ── Step 3: Confirmation ──────────────────────────────────────────────────────
  const ConfirmationStep = () => (
    <div className="space-y-6 animate-fadeIn">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircleIcon className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Confirm Selection</h2>
        <p className="text-sm text-gray-600">Review your bike details</p>
      </div>
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
          <div className="w-14 h-14 bg-blue-50 rounded-xl p-2 flex items-center justify-center flex-shrink-0">
            <img src={selectedBrand?.image || getFallbackImage(56, 56)} alt={selectedBrand?.name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => handleImageError(e, selectedBrand?.image)} />
          </div>
          <div className="flex-1">
            <p className="text-xs text-blue-600 font-bold uppercase mb-1">Brand</p>
            <p className="text-base font-bold text-gray-900">{selectedBrand?.name}</p>
          </div>
          <button onClick={() => { setCurrentStep(1); setSelectedModel(null); }}
            className="px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-semibold">Edit</button>
        </div>
        <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
          <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <CubeIcon className="h-7 w-7 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-blue-600 font-bold uppercase mb-1">Model</p>
            <p className="text-base font-bold text-gray-900">{selectedModel?.name}</p>
          </div>
          <button onClick={() => setCurrentStep(2)}
            className="px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-semibold">Edit</button>
        </div>
      </div>
      <ErrorBanner />
      <div className="space-y-3">
        <button onClick={handleCheckPrices} disabled={!selectedBrand || !selectedModel || loading}
          className={`w-full p-5 rounded-2xl font-bold text-base transition-all flex items-center justify-center gap-2 shadow-lg
            ${!selectedBrand || !selectedModel || loading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl"}`}>
          {loading
            ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Loading Services...</span></>
            : <><span>VIEW AVAILABLE SERVICES</span><ChevronRightIcon className="h-5 w-5" /></>}
        </button>
        <button onClick={() => setCurrentStep(0)}
          className="w-full p-3 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors font-semibold">
          Start Over
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full" id="quote-form">
      <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <h1 className="text-2xl font-bold mb-1">Book Your Service</h1>
          <p className="text-blue-100 text-sm">Quick & easy bike service booking</p>
        </div>
        <div className="px-6 pt-8"><ProgressStepper /></div>
        <div className="p-6">
          {currentStep === 0 && <InitialStep />}
          {currentStep === 1 && <BrandSelectionStep />}
          {currentStep === 2 && <ModelSelectionStep />}
          {currentStep === 3 && <ConfirmationStep />}
        </div>
        <div className="px-6 pb-6 pt-2 border-t border-gray-100">
          <p className="text-center text-xs text-gray-500">🔒 Secure & Trusted Service • 5000+ Happy Customers</p>
        </div>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #F3F4F6; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #BFDBFE; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #93C5FD; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default QuoteForm;
